/**
 * Sticky Header Module
 * Handles sticky header functionality when scrolling
 */
class StickyHeader {
    constructor(options = {}) {
        this.header = document.querySelector('.header');
        // Compensate on the header's direct parent (#panel), not <body>: padding on <body>
        // moves the whole #panel box down, which Chrome scores as a ~0.3 layout shift on mobile
        this.spacer = this.header ? this.header.parentElement : null;
        this.isSticky = false;
        this.scrollThreshold = options.scrollThreshold || 100; // Scroll threshold to trigger sticky behavior
        this.mobileScrollThreshold = options.mobileScrollThreshold || this.scrollThreshold; // Mobile-specific threshold
        this.enableMobile = options.enableMobile !== false; // Enable sticky on mobile by default
        this.lastScrollTop = 0;
        this.scrollDirection = 'down';
        this.mobileBreakpoint = 768; // Mobile breakpoint
        this.init();
    }

    init() {
        if (!this.header) return;
        
        // Pre-calculate header height to prevent CLS
        this.initialHeaderHeight = this.getHeaderHeight();
        
        // Enable GPU acceleration
        this.header.style.transform = 'translateZ(0)';
        this.header.style.backfaceVisibility = 'hidden';
        
        this.bindEvents();
        this.checkScrollPosition();
    }

    bindEvents() {
        // Throttle scroll event for better performance
        let ticking = false;
        
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.checkScrollPosition();
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });

        window.addEventListener('resize', () => {
            // Recalculate header height on resize to prevent CLS
            this.initialHeaderHeight = this.getHeaderHeight();
            this.checkScrollPosition();
        });
    }

    checkScrollPosition() {
        // Don't apply sticky on mobile if disabled
        if (!this.enableMobile && window.innerWidth <= this.mobileBreakpoint) {
            if (this.isSticky) {
                this.removeStickyClass();
            }
            return;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Determine scroll direction
        this.scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';
        this.lastScrollTop = scrollTop;
        
        // Use mobile threshold on mobile devices, desktop threshold otherwise
        const isMobile = window.innerWidth <= this.mobileBreakpoint;
        const activeThreshold = isMobile ? this.mobileScrollThreshold : this.scrollThreshold;
        
        if (scrollTop > activeThreshold && !this.isSticky) {
            this.addStickyClass();
        } else if (scrollTop <= activeThreshold && this.isSticky) {
            this.removeStickyClass();
        }
    }

    addStickyClass() {
        if (this.isSticky) return;
        
        // Calculate header height first
        const headerHeight = this.getHeaderHeight();
        
        // Set CSS custom property immediately
        document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
        
        // Use requestAnimationFrame for smoother transitions
        requestAnimationFrame(() => {
            // Add padding to the header's parent FIRST to prevent jump
            this.spacer.style.paddingTop = headerHeight + 'px';
            
            // Then apply sticky class in the same frame
            this.header.classList.add('header--sticky');
            this.isSticky = true;
            
            // Add scroll direction class for additional styling
            this.header.classList.add(`header--scroll-${this.scrollDirection}`);
            
            // Trigger custom event for other modules
            window.dispatchEvent(new CustomEvent('headerSticky', { 
                detail: { 
                    isSticky: true, 
                    scrollDirection: this.scrollDirection 
                } 
            }));
        });
    }

    removeStickyClass() {
        if (!this.isSticky) return;
        
        // Use requestAnimationFrame for smoother transitions
        requestAnimationFrame(() => {
            this.header.classList.remove('header--sticky');
            this.isSticky = false;
            
            // Remove scroll direction classes
            this.header.classList.remove('header--scroll-up', 'header--scroll-down');
            
            // Remove padding from the header's parent
            this.spacer.style.paddingTop = '';
            
            // Reset CSS custom property
            document.documentElement.style.setProperty('--header-height', 'auto');
            
            // Trigger custom event for other modules
            window.dispatchEvent(new CustomEvent('headerSticky', { 
                detail: { 
                    isSticky: false, 
                    scrollDirection: this.scrollDirection 
                } 
            }));
        });
    }

    // Get accurate header height accounting for mobile differences
    getHeaderHeight() {
        // Temporarily remove sticky class to get natural height
        const wasSticky = this.header.classList.contains('header--sticky');
        if (wasSticky) {
            this.header.classList.remove('header--sticky');
        }
        
        // Force reflow to ensure accurate measurement
        this.header.offsetHeight;
        
        const height = this.header.offsetHeight;
        
        // Restore sticky class if it was there
        if (wasSticky) {
            this.header.classList.add('header--sticky');
        }
        
        return height;
    }

    // Public method to manually check sticky state
    getStickyState() {
        return this.isSticky;
    }

    // Public method to set scroll threshold
    setScrollThreshold(threshold) {
        this.scrollThreshold = threshold;
    }

    // Public method to get scroll direction
    getScrollDirection() {
        return this.scrollDirection;
    }

    // Public method to enable/disable mobile sticky
    setMobileEnabled(enabled) {
        this.enableMobile = enabled;
        if (!enabled && this.isSticky) {
            this.removeStickyClass();
        }
    }
}

export default StickyHeader; 